import React, { useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import {
    Users,
    CheckSquare,
    Calendar,
    Clock,
    Package,
    BarChart3,
    Mail,
} from "lucide-react";
import liriworksImage from "../assets/hrm-image.png";
import apiService from "services/apiService";
import { useToaster } from "contexts/toasterContext";
import { emailSchema } from "util/validation";

const validationSchema = yup.object({
    // CHANGE: yup's built-in .email() accepts user@localhost and
    // abc@gmail.commmmmmmmmmm. Uses the shared schema instead.
    email: emailSchema({
      requiredMessage: "Email is required",
      invalidMessage: "Invalid email",
    }),
});

type FormData = yup.InferType<typeof validationSchema>;

type InputProps = {
    name: keyof FormData;
    label: string;
    placeholder?: string;
    required?: boolean;
};

const Input = ({ name, label, placeholder, required = false }: InputProps) => {
    const {
        register,
        formState: { errors },
    } = useFormContext<FormData>();

    const fieldError = errors[name];
    const error =
        typeof fieldError?.message === "string" ? fieldError.message : undefined;

    return (
        <div className="space-y-1.5 w-full">
            <label className="text-[13px] font-semibold text-[#344567] ml-1 dark:text-slate-200">
                {label} {required ? <span className="text-red-500">*</span> : null}
            </label>

            <div className="relative group">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9aa8c1] group-focus-within:text-blue-600">
                    <Mail size={18} />
                </div>

                <input
                    {...register(name)}
                    type="email"
                    placeholder={placeholder}
                    className={`w-full pl-11 pr-4 py-3 bg-white dark:bg-slate-800 border ${error ? "border-red-500" : "border-[#d9e2f0] dark:border-slate-600"
                        } rounded-xl text-sm text-[#0f1b35] dark:text-slate-100 focus:!ring-0 focus:!shadow-none focus:!outline-none ${error ? "focus:!border-red-500" : "focus:!border-[#d9e2f0]"
                        }`}
                />
            </div>

            {error && <p className="text-xs text-red-500 ml-1">{error}</p>}
        </div>
    );
};

const features = [
    { icon: Users, label: "Employee & Profile Management" },
    { icon: CheckSquare, label: "Task & Project Tracking" },
    { icon: Calendar, label: "Leave & Reimbursement Management" },
    { icon: Clock, label: "Timesheet & Work Hour Tracking" },
    { icon: Package, label: "Inventory & Asset Management" },
    { icon: BarChart3, label: "Shift & Roster Scheduling" },
];

export default function ForgotPassword() {
    const navigate = useNavigate();
    const { addToast } = useToaster();
    const [submitting, setSubmitting] = useState(false);

    const methods = useForm<FormData>({
        resolver: yupResolver(validationSchema),
    });

    const onSubmit = async (data: FormData) => {
        if (submitting) return;

        setSubmitting(true);
        try {
            const queryParams = new URLSearchParams({
                email: data.email.trim(),
                redirectBase: window.location.origin,
            });

            await apiService.post(
                `api/auth/forgot-password?${queryParams.toString()}`,
                {}
            );
            addToast("Password reset link sent to your email", "success");
            navigate("/login");
        } catch {
            addToast("Unable to process forgot password request", "error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="h-screen w-full overflow-y-auto sm:overflow-hidden flex items-center justify-center px-4 py-5 bg-slate-50 dark:bg-slate-900">
            <div className="w-full max-w-6xl h-[90vh] max-h-[760px] rounded-[26px] overflow-hidden border border-[#dbe4f2] dark:border-slate-700 bg-gradient-to-br from-[#eaf1fd] via-[#f1f5fd] to-[#e6effd] dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 shadow-[0_20px_50px_rgba(16,45,90,0.12)]">
                <div className="grid sm:grid-cols-[1.1fr_0.9fr] h-full">
                    <div className="relative px-7 pt-7 pb-44 lg:px-8 lg:pt-8 lg:pb-44 overflow-hidden">
                        <div className="flex items-center gap-3 mb-5">
                            <svg
                                viewBox="0 0 64 64"
                                className="w-10 h-10"
                                aria-hidden="true"
                                focusable="false"
                            >
                                <path
                                    d="M10 8c0-3.3 2.7-6 6-6h8v44h18c3.3 0 6 2.7 6 6v6H16c-3.3 0-6-2.7-6-6V8z"
                                    fill="#2563EB"
                                />
                                <rect x="28" y="12" width="14" height="14" rx="4" fill="#2563EB" />
                                <rect x="44" y="12" width="14" height="14" rx="4" fill="#2563EB" />
                                <rect x="28" y="28" width="14" height="14" rx="4" fill="#2563EB" />
                                <path d="M44 28h8l6 6v8h-8a6 6 0 0 1-6-6v-8z" fill="#2563EB" />
                            </svg>
                            <div>
                                <div className="text-[30px] leading-[1] font-bold text-[#172647] dark:text-slate-100">
                                    Liriworks
                                </div>
                                <div className="text-[12px] text-[#5a6b89] dark:text-slate-300">
                                    All-in-one platform
                                </div>
                            </div>
                        </div>

                        <h1 className="text-[48px] leading-[1.05] font-bold text-[#122348] dark:text-slate-100 max-w-[620px]">
                            Manage Your Workforce
                            <br />
                            Smarter
                        </h1>

                        <p className="mt-3 text-[22px] leading-[1.45] text-[#3b4f74] dark:text-slate-300 max-w-[650px]">
                            Liriworks is a powerful platform that helps organizations manage employees, tasks,
                            timesheets, leaves, and performance - all in one place.
                        </p>

                        <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3 max-w-[650px]">
                            {features.map((f) => (
                                <div key={f.label} className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-xl bg-white dark:bg-slate-800 border border-[#dfe8f5] dark:border-slate-700 flex items-center justify-center shadow-sm flex-shrink-0">
                                        <f.icon className="text-[#2b63e7] w-4 h-4" />
                                    </div>
                                    <span className="text-[15px] leading-tight font-semibold text-[#2b3f62] dark:text-slate-200">
                                        {f.label}
                                    </span>
                                </div>
                            ))}
                        </div>

                        <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
                            <img
                                src={liriworksImage}
                                alt="Human Resource Management dashboard preview"
                                className="w-full h-[175px] object-cover object-center"
                            />
                        </div>
                    </div>

                    <div className="px-5 py-6 lg:px-7 lg:py-7 flex items-center justify-center">
                        <div className="w-full max-w-[420px] bg-white dark:bg-slate-900 rounded-2xl border border-[#dbe3f1] dark:border-slate-700 shadow-[0_16px_30px_rgba(17,46,96,0.10)] p-6 lg:p-7">
                            <h2 className="text-[36px] leading-tight font-bold text-[#122348] dark:text-slate-100 mb-1">
                                Forgot Password?
                            </h2>
                            <p className="text-[17px] text-[#667a9a] dark:text-slate-300 mb-5">
                                Enter your email to receive a reset link
                            </p>

                            <FormProvider {...methods}>
                                <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-5">
                                    <Input
                                        name="email"
                                        label="Email address"
                                        placeholder="Enter your email"
                                        required
                                    />

                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="w-full !bg-[#2a62e6] !text-white border border-[#2a62e6] py-3 rounded-xl font-semibold shadow-[0_8px_18px_rgba(34,88,212,0.25)] hover:!bg-[#2258d4] hover:border-[#2258d4] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                                    >
                                        {submitting ? "Submitting..." : "Submit"}
                                    </button>

                                    <div className="text-center">
                                        <Link
                                            to="/login"
                                            className="text-[#2a62e6] font-semibold hover:underline"
                                        >
                                            Back to Login
                                        </Link>
                                    </div>
                                </form>
                            </FormProvider>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
